<template>
  <section class="handoff">
    <form v-if="allowAddComment" class="form" @submit.prevent>
      <WTextarea
        ref="textarea"
        v-model="newComment"
        class="textarea"
        label="Novo comentário"
        placeholder="Descreva os acontecimentos mais importantes desse atendimento"
        rows="2"
        :max-length="240"
      />

      <div class="handoff__comment-bottom">
        <WButton
          size="small"
          class="button"
          variant="cta"
          :disabled="disableForm || loading"
          :loading="loading"
          @click="saveComment"
        >
          Salvar
        </WButton>

        <span>Limite de 240 caracteres</span>
      </div>
    </form>

    <template v-if="handoff && handoff.length">
      <article v-for="item in handoffMapped" :key="item.id" class="container">
        <WAvatar
          v-if="item.type !== 'FUP_QUESTION'"
          size="small"
          :url="getProfilePhoto(item)"
        />
        <WAvatar
          v-else
          size="small"
          :url="require('@/assets/channels-avatar.svg')"
        />

        <div class="content">
          <header class="header">
            <span>{{ getProfileName(item) + ' ' }}</span>

            <span class="info">
              {{ handoffInfoTypes[item.type] }} •
              {{ item.createdAtFormatted }}
            </span>
          </header>
          <WParagraph v-if="!item.answer" class="comment">
            {{ item.comment }}
          </WParagraph>
          <template v-else>
            <WParagraph class="question">
              {{ item.question }}
            </WParagraph>
            <WParagraph class="answer">- {{ item.answer }}</WParagraph>
          </template>
        </div>
      </article>
    </template>

    <article v-else class="container" data-align="center">
      <WAvatar size="small" :url="require('@/assets/tea.svg')" />

      <WParagraph>Não existem comentários cadastrados no canal</WParagraph>
    </article>

    <article class="container" data-align="center">
      <div class="icon">
        <WIcon icon="icAliceAgora" />
      </div>

      <WParagraph>
        Início do atendimento
        <span class="date"> • {{ createdAt }} </span>
      </WParagraph>
    </article>
  </section>
</template>

<script>
import {
  WAvatar,
  WButton,
  WIcon,
  WParagraph,
  WTextarea
} from '@alice-health/wonderland-vue'
import format from 'date-fns/format'
import { mapGetters, mapState } from 'vuex'

export default {
  components: {
    WIcon,
    WButton,
    WTextarea,
    WAvatar,
    WParagraph
  },
  props: {
    channel: { type: Object, default: undefined },
    staffId: { type: String, default: undefined },
    allowAddComment: Boolean
  },
  data() {
    return {
      loading: false,
      newComment: '',
      handoffNameTypes: {
        FUP_ANSWER: '',
        FUP_QUESTION: 'Time de Saúde'
      },
      handoffInfoTypes: {
        COMMENT: 'adcionou um comentário',
        FUP_ANSWER: 'respondeu um FUP',
        FUP_QUESTION: 'enviou um FUP'
      }
    }
  },
  computed: {
    ...mapState('pillars/channel/handoff', ['handoff']),
    ...mapGetters('member', ['getMemberById']),
    disableForm() {
      return !this.newComment.length || this.$refs.textarea?.invalid
    },
    handoffMapped() {
      return this.handoff.map(item => {
        const [question, answer] =
          (item.type === 'FUP_ANSWER' && item.comment?.split('•')) || []

        return {
          ...item,
          question,
          answer,
          staff: item.type === 'COMMENT' && this.channel.staffs[item?.staffId],
          createdAtFormatted: format(
            new Date(`${item?.createdAt}Z`),
            "dd/MM/yy 'às' HH:mm"
          )
        }
      })
    },
    personFullName() {
      return this.person?.fullName
    },
    person() {
      return this.getMemberById({
        channelPersonId: this.channel.channelPersonId
      })
    },
    createdAt() {
      return format(this.channel.createdAt.toDate(), "dd/MM/yyyy 'às' HH:mm")
    }
  },
  mounted() {
    this.init()
  },
  beforeUnmount() {
    this.$store.dispatch('pillars/channel/handoff/clearHandoff')
  },
  methods: {
    saveComment() {
      try {
        this.loading = true

        this.$store.dispatch('pillars/channel/handoff/addComment', {
          comment: this.newComment,
          channelId: this.channel.id,
          personId: this.channel.personId
        })

        this.newComment = ''
      } finally {
        this.loading = false
      }
    },
    getProfilePhoto(handoff) {
      return handoff.staff?.profileImageUrl || this.person?.photoURL
    },
    getProfileName(handoff) {
      if (handoff.type === 'COMMENT' && handoff.staff) {
        const { firstName, lastName } = handoff.staff
        return `${firstName} ${lastName}`
      }

      return this.handoffNameTypes[handoff.type]
    },
    async init() {
      await this.$store.dispatch(
        'pillars/channel/handoff/fetch',
        this.channel.id
      )

      const staffIds = this.handoff?.map(comment => comment.staffId)

      if (staffIds) {
        this.$store.dispatch('staff/getStaffs', { staffIds })
      }

      this.handoffNameTypes.FUP_ANSWER = this.personFullName
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/style/_non-rendering';
.handoff {
  @extend %body-small;
  background: var(--sys-color-surface-background-default);
  color: var(--sys-color-content-primary);
  padding: var(--gl-spacing-04);
  height: calc(100% - var(--filter-header-height));
  overflow: auto;

  &__comment-bottom {
    align-items: center;
    display: flex;
    justify-content: space-between;
  }
}

.title {
  @extend %title-small;
  margin-bottom: var(--gl-spacing-08);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.button {
  margin-top: var(--gl-spacing-02);
}

.info,
.date,
.question {
  color: var(--sys-color-content-secondary);
  font-feature-settings: normal;
}

.answer {
  margin-top: var(--gl-spacing-01);
}

.form {
  padding-bottom: var(--gl-spacing-08);
  margin-bottom: var(--gl-spacing-08);
  border-bottom: 1px solid var(--gl-color-shades-gray-20);
}

.textarea > div {
  min-height: var(--gl-spacing-12);
}

.container {
  margin-bottom: var(--gl-spacing-06);
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: var(--gl-spacing-04);
  position: relative;

  &[data-align='center'] {
    align-items: center;
  }

  p {
    margin: 0;
  }
}

.header {
  margin-bottom: var(--gl-spacing-02);
}

.icon {
  color: var(--gl-color-shades-magenta-60);
  margin: auto;
}

.content {
  margin-top: var(--gl-spacing-02);
}
</style>
